import {receive, iceCandidate, unMuteVideo, muteVideo} from './conference-websocket.js'
import config from '../../../static/config/index.js'

/**
 * Prefer H264 in SDP by moving its payload types to the front of m=video.
 */
function preferH264InSdp(sdp) {
  if (!sdp || typeof sdp !== 'string') {
    return sdp
  }
  const eol = sdp.indexOf('\r\n') !== -1 ? '\r\n' : '\n'
  const lines = sdp.split(eol)
  const mLineIndex = lines.findIndex(line => line.startsWith('m=video'))
  if (mLineIndex === -1) {
    return sdp
  }

  const h264PayloadTypes = []
  for (const line of lines) {
    const match = line.match(/^a=rtpmap:(\d+)\s+H264\/\d+/i)
    if (match) {
      h264PayloadTypes.push(match[1])
    }
  }
  if (!h264PayloadTypes.length) {
    return sdp
  }

  const parts = lines[mLineIndex].split(' ')
  if (parts.length <= 3) {
    return sdp
  }
  const header = parts.slice(0, 3)
  const payloads = parts.slice(3)
  const preferred = payloads.filter(pt => h264PayloadTypes.indexOf(pt) !== -1)
  const rest = payloads.filter(pt => h264PayloadTypes.indexOf(pt) === -1)
  lines[mLineIndex] = header.concat(preferred, rest).join(' ')
  return lines.join(eol)
}

class Participant {
  name = null
  videoId = null
  audioId = null
  muted = false
  speaking = false
  muteVideo = false
  deptName = null
  deptI18n=false
  deptId = null
  roomName = null
  isVcs = false
  isTemp = false
  isGrade = false
  isShare = false
  isShowShare = true
  number = null
  //1分机 2通讯录 3号码 4摄像头 5 GB28181 6调度员 7 screen
  type = null
  status = ''
  gradeNumber = ''
  gradeName = ''
  net=''
  icon=''
  meetingIcon=''
  order = 1
  isRtsp = false
  realName = null
  playing = false
  _screenStream = null
  _screenTrack = null
  _buildingPeer = false
  _disposed = false
  /** ICE candidates received before remote description is set */
  _pendingCandidates = null
  /** Whether SDP answer / remote description has been applied */
  _remoteDescSet = false
  /** iceConnectionState 进入 checking 的时间，用于超时后重建 */
  _iceCheckingSince = 0
  iceCheckingTimeoutMs = 12000


  constructor(name,roomName,number,type,isVcs,gradeNumber) {
    this.name = name
    this.realName = name
    this.roomName = roomName
    this.number = number
    this.type = type ? Number(type) : null
    this.isVcs = isVcs
    this.videoId = `video-${name}`
    this.audioId = `audio-${name}`
    this.muted = false
    this.speaking = false
    this.muteVideo = false
    this.gradeNumber = gradeNumber
    this._pendingCandidates = []
    this._remoteDescSet = false
    this._iceCheckingSince = 0
    if (this.gradeNumber) {
      this.isGrade = true
    }
    Object.defineProperty(this,'rtcPeer',{writable:true,configurable:true})
    Object.defineProperty(this,'speakHark',{writable:true,configurable:true})

  }

  /** Stable identity used for Vue keys / map lookup */
  get identity() {
    if ((this.type == 1 || this.type == 2) && this.number) {
      return String(this.number)
    }
    return String(this.name)
  }

  setStatus(status) {
    this.status = status
    if (this.status !== 'Talking') {
      this.meetingIcon = ''
    }
    switch (this.status) {
      case 'Talking':
        this.order = 0
        break
      case 'Broading':
        this.order = 1
        break
      case 'Idle':
        this.order = 2
        break
      case 'OffLine':
        this.order = 3
        break
      case 'UnKnow':
        this.order = 4
        if (this.type == '4') {
          this.isRtsp = true
        }
        break
    }
  }

  setMute(muted) {
    this.muted = muted
    if (this.muted) {
      if (this.meetingIcon) {
        this.meetingIcon = 'conf_mute'
      }
    }else {
      if (this.meetingIcon) {
        this.meetingIcon = 'conf'
      }
    }
  }

  mutedAudio(enableAudio) {
    if (this.rtcPeer) {
      this.rtcPeer.peerConnection.getSenders().forEach(sender => {
        if (sender.track.kind == 'audio') {
          sender.track.enabled = enableAudio
          this.muted = !enableAudio
        }
      })
    }
  }

  setDept(deptId, deptName) {
    this.deptId = deptId
    this.deptName = deptName
    if (!this.deptName && this.type==2) {
      this.deptName = 'UnKnow'
      this.deptI18n = true
      return
    }
    this.deptI18n = this.deptName == 'all_area' || this.deptName == 'only_admin';
  }

  getVideoElement() {
    return document.getElementById(this.videoId)
  }

  getAudioElement() {
    return document.getElementById(this.audioId)
  }

  getMediaStream() {
    if (!this.rtcPeer) return null
    try {
      if (this.rtcPeer.getLocalStream && this.rtcPeer.getLocalStream()) {
        return this.rtcPeer.getLocalStream()
      }
      if (this.rtcPeer.getRemoteStream && this.rtcPeer.getRemoteStream()) {
        return this.rtcPeer.getRemoteStream()
      }
    } catch (e) {
      console.warn('getMediaStream error', e)
    }
    return null
  }

  isPeerAlive() {
    return this.canReusePeer() || this.isNegotiationInProgress()
  }

  noteIceState(state) {
    if (state === 'checking') {
      if (!this._iceCheckingSince) {
        this._iceCheckingSince = Date.now()
      }
      return
    }
    this._iceCheckingSince = 0
  }

  isCheckingTimedOut() {
    const pc = this.rtcPeer && this.rtcPeer.peerConnection
    if (!pc || pc.iceConnectionState !== 'checking') {
      return false
    }
    if (!this._iceCheckingSince) {
      this._iceCheckingSince = Date.now()
      return false
    }
    return Date.now() - this._iceCheckingSince >= this.iceCheckingTimeoutMs
  }

  /**
   * 仅 connected / completed，以及 disconnected（ICE 可自行恢复）时复用。
   * checking 超时与 failed 不可复用。
   */
  canReusePeer() {
    if (this._disposed || !this.rtcPeer || !this.rtcPeer.peerConnection) return false
    const pc = this.rtcPeer.peerConnection
    if (pc.signalingState === 'closed') return false
    const ice = pc.iceConnectionState
    const conn = pc.connectionState
    if (ice === 'failed' || ice === 'closed' || conn === 'failed' || conn === 'closed') return false
    return ice === 'connected' || ice === 'completed' || ice === 'disconnected'
  }

  /** new，或尚未超时的 checking：协商进行中，不要并行再建一条 peer */
  isNegotiationInProgress() {
    if (this._disposed || !this.rtcPeer || !this.rtcPeer.peerConnection) return false
    const pc = this.rtcPeer.peerConnection
    if (pc.signalingState === 'closed') return false
    const ice = pc.iceConnectionState
    const conn = pc.connectionState
    if (ice === 'failed' || ice === 'closed' || conn === 'failed' || conn === 'closed') return false
    if (ice === 'new') return true
    if (ice === 'checking') return !this.isCheckingTimedOut()
    return false
  }

  /** checking 超时 / failed / closed：拆掉后重建 */
  needsPeerRebuild() {
    if (this._disposed || !this.rtcPeer || !this.rtcPeer.peerConnection) return false
    const pc = this.rtcPeer.peerConnection
    const ice = pc.iceConnectionState
    const conn = pc.connectionState
    if (pc.signalingState === 'closed' || ice === 'failed' || ice === 'closed' || conn === 'failed' || conn === 'closed') {
      return true
    }
    return ice === 'checking' && this.isCheckingTimedOut()
  }

  /** Merge non-media fields from another participant instance (in-place update) */
  mergeFrom(source) {
    if (!source || source === this) return
    const fields = [
      'muted', 'muteVideo', 'deptName', 'deptI18n', 'deptId', 'status',
      'meetingIcon', 'icon', 'net', 'gradeName', 'gradeNumber', 'order',
      'isTemp', 'isShare', 'isShowShare', 'isRtsp', 'realName', 'isVcs',
      'isGrade', 'type', 'number'
    ]
    fields.forEach(f => {
      if (source[f] !== undefined && source[f] !== null) {
        this[f] = source[f]
      }
    })
    if (source.status) {
      this.setStatus(source.status)
    }
  }

  queueIceCandidate(candidate) {
    if (!this._pendingCandidates) {
      this._pendingCandidates = []
    }
    this._pendingCandidates.push(candidate)
  }

  flushIceCandidates() {
    if (!this._pendingCandidates || this._pendingCandidates.length === 0) {
      return
    }
    // 没有 peer 时保留队列，禁止清空丢弃
    if (!this.rtcPeer) {
      return
    }
    if (this._flushingIce) {
      return
    }
    this._flushingIce = true
    try {
      while (this._pendingCandidates.length) {
        if (!this.rtcPeer) {
          break
        }
        const candidate = this._pendingCandidates.shift()
        try {
          // kurento 在 remote description 就绪前只内部缓冲，callback 会推迟到 stable。
          // 不能用这个 callback 判断本批结束并删除队列。
          this.rtcPeer.addIceCandidate(candidate, (error) => {
            if (error) {
              console.warn('flushIceCandidates addIceCandidate failed:', error)
            }
          })
        } catch (e) {
          this._pendingCandidates.unshift(candidate)
          console.warn('flushIceCandidates exception:', e)
          break
        }
      }
    } finally {
      this._flushingIce = false
      if (this.rtcPeer && this._pendingCandidates && this._pendingCandidates.length) {
        this.flushIceCandidates()
      }
    }
  }

  markRemoteDescriptionSet() {
    this._remoteDescSet = true
    this.flushIceCandidates()
  }

  resetNegotiationState() {
    this._remoteDescSet = false
    this._iceCheckingSince = 0
    // 保留尚未交给 peer 的 candidate，避免重建窗口内 trickle 被清空
  }

  offerToReceiveVideo(error, offerSdp, wp) {
    if (error) return console.error('sdp offer error',error)
    // 优先 H264 编码：受 static/config preferH264 控制
    const sdp = config.preferH264 ? preferH264InSdp(offerSdp) : offerSdp
    receive(this.type == '1' || this.type==2 ? this.number : this.name, sdp, !this.isShowShare ? "1" : "0")
    // 仅重置 remoteDesc 标记；保留已提前到达的 ICE，待 answer 成功后 flush
    this._remoteDescSet = false
  }

  onIceCandidate(candidate, wp) {
    iceCandidate(candidate, this.type == '1'|| this.type==2 ? this.number : this.name, !this.isShowShare ? "1" : "0")
  }

  dispose(unReset) {
    if (this.rtcPeer) {
      try {
        // 1. 停止所有音视频轨道
        const stream = this.rtcPeer.getLocalStream() || this.rtcPeer.getRemoteStream();
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }

        // 2. 调用 kurento 的销毁方法
        this.rtcPeer.dispose();
      } catch (e) {
        console.error('Error during rtcPeer disposal', e);
      } finally {
        // 3. 彻底删除 rtcPeer 属性，而非仅赋值为 null
        delete this.rtcPeer;
        // 4. 清理创建锁标志
        delete this._buildingPeer;
        this._disposed = true
        this.resetNegotiationState()
      }
    }
    if (this._screenStream) {
      this._screenStream.getTracks().forEach(track => {
        track.stop();
        track.onended = null; // 移除事件监听
      });
      delete this._screenStream;
      delete this._screenTrack;
    }
    if (this.speakHark) {
      this.speakHark.stop()
      this.speakHark = null
    }
    if (!unReset) {
      this.muted = false
      this.speaking = false
      this.muteVideo = false
      this.playing = false
    }

  }
}
export default Participant



// WEBPACK FOOTER //
// ./src/view/conference/participant.js